import Axios, { AxiosInstance } from 'axios';
import { publicPath } from '@nab/nab-x-sdk-core';
import { DAF_GRANT_TYPE, DAF_SUBJECT_TOKEN_TYPE, DAF_TOKEN_TYPE, DAF_TOKEN_RETRY_COUNT_MAX } from './constants';
import { AppLoader } from '../AppLoader';
import IAppScope from '../common/types/IAppScope';
import { ITokenConfig } from './IRequestTokenDetails';
import { sdkLoggerInterface as log } from '../Logger/Logger';

const cacheScopes = (tag: string, scopes: IAppScope[]) => {
    const appCache = AppLoader.getConfig(tag);
    appCache.scopes = scopes;
};

const loadScopes = async (tag: string, tokenExHttpClientInstance: AxiosInstance): Promise<IAppScope[]> => {
    const response = await tokenExHttpClientInstance.get<IAppScope[]>(`${publicPath(tag)}scopes.json`);
    if (response && response.status >= 200 && response.status < 400 && response.data) {
        cacheScopes(tag, response.data);
        return response.data;
    }
    if (response && response.status === 404) {
        return Promise.reject(new Error(`No scopes file defined for ${tag}`));
    }
    return Promise.reject(new Error(`Error reading scopes files for ${tag}`));
};

const getMiniAppScope = async (tag: string, seedContext: string, tokenExHttpClientInstance: AxiosInstance): Promise<IAppScope> => {
    let appScopes = AppLoader.getConfig(tag) ? AppLoader.getConfig(tag).scopes : null;
    if (!appScopes) {
        try {
            appScopes = await loadScopes(tag, tokenExHttpClientInstance);
        } catch (e) {
            log.error(`Scopes for mini-app ${tag}, unavailable for this reason: ${e.message}`, e);
            if (e.message.startsWith(`No scopes`)) {
                return { context: 'noscopes', clientId: 'noscopes', scopes: ['noscopes'] };
            }
            return null;
        }
    }

    return appScopes.filter((scope) => scope.context === seedContext)[0];
};

const createDAFRequestPayload = (seedToken: string, miniAppScope: IAppScope) => {
    return {
        client_id: miniAppScope.clientId,
        grant_type: DAF_GRANT_TYPE,
        requested_token_type: DAF_TOKEN_TYPE,
        subject_token: miniAppScope.clientId,
        subject_token_type: DAF_SUBJECT_TOKEN_TYPE,
        actor_token: seedToken,
        actor_token_type: DAF_TOKEN_TYPE,
        scope: miniAppScope.scopes.join(' ')
    };
};

async function tokenExchangeCall(
    tokenConfig: ITokenConfig,
    miniAppScope: IAppScope,
    tokenExHttpClientInstance: AxiosInstance,
    seedTokenProvider?: () => Promise<string>,
    retryCount: number = 0
) {
    const { seedToken, tokenEndpoint } = tokenConfig;
    const payload = createDAFRequestPayload(seedToken, miniAppScope);
    try {
        const response = await tokenExHttpClientInstance.post(tokenEndpoint, payload, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data.access_token;
    } catch (e) {
        // if status is 401 assume that shell token has expired and await till a new token has been retrieved.
        if (e.status === 401 && seedTokenProvider && retryCount < DAF_TOKEN_RETRY_COUNT_MAX) {
            const renewedSeedToken: string = await seedTokenProvider();
            AppLoader.SeedToken = renewedSeedToken;
            // before new token exchange replace the shell token
            return tokenExchangeCall(
                { ...tokenConfig, seedToken: renewedSeedToken },
                miniAppScope,
                tokenExHttpClientInstance,
                seedTokenProvider,
                retryCount + 1 // increase current retry count
            );
        }
        /**
         * If the status code is 429 the rate limit has been triggered and wait till the rate limit has been lifted.
         * Refer here for exact docs: https://github.aus.thenational.com/pages/ATNOC/stardocs/publish-apis/#service-rate-limiting
         */
        if (e.status === 429 && retryCount < DAF_TOKEN_RETRY_COUNT_MAX) {
            // Wait 1 Second before attempting a tokenExchange call Again.
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return tokenExchangeCall(tokenConfig, miniAppScope, tokenExHttpClientInstance, seedTokenProvider, retryCount + 1);
        }
        // once retrieved use recursive to re-initiate this token exchange.
        return Promise.reject(e);
    }
}

/**
 * [[include:packages/consumer/sdk-browser/src/TokenExchanger/README.md]]
 */
const TokenExchanger = {
    /**
     * Generates and returns the mini-app token.
     * Returns seed token, if mini-app scopes are not defined / provided
     * @deprecated since version 12.0.0; Please use `@nab/x-runtime` instead. Initialise the x-runtime with `DafExchangeStrategy` and invoke `authenticate` action to obtain a token. Refer the NAB-X {@link https://github.aus.thenational.com/pages/nab-x/nab-x-quickstarts/create/shells/shell-x-runtime.html quickstart} for more info.
     * @param tag - mini-app name/tag
     * @param {ITokenConfig} tokenConfig - Contains the relevant information for the token exchange.
     * @param {Promise<string>} seedTokenProvider - This is a function passed into the token exchange to get a new seed/actor token, Must return a promise with a string.
     * @return {String} mini-app token.
     * @example
     * ### Basic Implementation
     * ```js
     * TokenExchange.generateMiniAppToken('mini-app', {
     *    tokenEndpoint: 'https://nab-daf.com.au/token/exchange',
     *    seedToken: 'seedToken',
     *    context: 'external'
     *   })
     *    .then(token => {
     *        return token;
     *    })
     *    .catch(error => {
     *        console.error(error);
     *    });
     * ```
     * @example
     * ### 401 Retry example with seedTokenProvider
     * ```js
     * function newSeedToken() {
     *    return 'thisIsANewToken';
     *}
     *
     * TokenExchange.generateMiniAppToken('mini-app', {
     *    tokenEndpoint: 'https://nab-daf.com.au/token/exchange',
     *    seedToken: 'seedToken',
     *    context: 'external'
     *    },
     *    seedTokenProvider: newSeedToken
     *)
     *   .then(token => {
     *        return token;
     *    })
     *    .catch(error => {
     *        console.error(error);
     *    });
     * ```
     */
    generateMiniAppToken: async (tag: string, tokenConfig: ITokenConfig, seedTokenProvider?: () => Promise<string>): Promise<string> => {
        // create a separate instance of axios so that it is not affected by any issues on the default Axios instance. #DIGF1-741
        const tokenExHttpClientInstance = Axios.create();

        // Skip the tokenExchange and return the seedToken.
        // More Info can be found: https://jira.dss.ext.national.com.au/browse/DIGF1-536
        const { skipTokenExchange = false, seedToken, context, refreshToken = false } = tokenConfig;
        if (skipTokenExchange) {
            // Adding this refreshToken so that on first use of this method it does not use the seedTokenProvider,
            // When this is used inside the apploader.requestToken we will set this refresh token to true and use the seedTokenProvider
            // along with if seedToken is empty proceed with seedTokenProvider

            if (seedTokenProvider && refreshToken) {
                AppLoader.SeedToken = await seedTokenProvider();
                return AppLoader.SeedToken;
            }
            if (!seedToken) {
                AppLoader.SeedToken = await seedTokenProvider();
                return AppLoader.SeedToken;
            }
            return seedToken;
        }
        // if the seedToken is empty and the seedTokenProvider is empty then return here.
        if (!seedToken && !seedTokenProvider) {
            return '';
        }
        // if this is not empty use the seedTokenProvider to get a seed Token.
        if (!seedToken && seedTokenProvider) {
            AppLoader.SeedToken = await seedTokenProvider();
        }

        const miniAppScope = await getMiniAppScope(tag, context, tokenExHttpClientInstance);

        /**
         * TODO: Plan on removing this, as this is added only for backwards compatibility.
         *       Effectively we should send back some info saying that the app does not have any scopes to be used for token exchange
         */
        if (miniAppScope && miniAppScope.clientId === 'noscopes') {
            log.info(
                'Sending back Shell Token for backwards compatibility. Please make sure that your mini-app has a scopes JSON file to generate token via Token Exchanger'
            );
            return seedToken;
        }

        if (miniAppScope && miniAppScope.clientId !== 'noscopes') {
            return tokenExchangeCall(tokenConfig, miniAppScope, tokenExHttpClientInstance, seedTokenProvider);
        }

        return '';
    }
};

export { TokenExchanger };
